import Phaser from 'phaser';
import { BIOMES, type BiomeId } from '../../content/biomes';
import { MOWER_DEFS, type MowerId } from '../../content/mowers';
import { TILE_BY_ID } from '../../content/tiles';
import {
  ALL_PIECES,
  checkLawn,
  erase,
  FIRST_MET,
  lawnName,
  lawnSize,
  moveStart,
  newCustomLawn,
  kitOf,
  pieceKey,
  pieceUsable,
  placePiece,
  resizeLawn,
  startOf,
  storeLawn,
  tileAt,
  type CustomLawn,
  type Piece,
} from '../../meta/customLawns';
import { campaignLevelId } from '../../meta/economy';
import { STAR } from '../../meta/save/schema';
import { Dir } from '../../sim/direction';
import { isSolid, Tile } from '../../sim/grid';
import { TILE_SIZE } from '../layout';
import { BLADES_KEY } from '../render/bladesArt';
import {
  CRITTER_FRAME,
  CRITTERS_KEY,
  mowerTextureKey,
  PICKUP_FRAME,
  PICKUPS_KEY,
  TILES_KEY,
} from '../render/placeholderArt';
import { PROP_LIFT, propFor, propFrame, PROPS_KEY } from '../render/props';
import { frameIndex, tileFrame } from '../render/tileFrames';
import { saves } from '../records';
import { bevelButton, clearScene } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { drawPadlock } from '../ui/widgets';
import { wrapLineCount } from '../ui/wrap';
import { drawWorldIcon } from '../ui/zoneArt';
import { wipeTo } from './WipeScene';
import { playCustomLawn, REG_BUILDER_LAWN } from './customPlay';
import { lawnLink } from '../../meta/lawnCode';
import { shareLink } from '../../platform/share';

export interface BuilderData {
  /** The lawn to edit (else the one last open, from playing it). */
  lawnId?: string;
  /** Start a new lawn. */
  fresh?: boolean;
}

type Category = 'ground' | 'things' | 'items' | 'animals';
type ToolMode = 'paint' | 'erase' | 'start' | 'pan';

const CATEGORIES: readonly { id: Category; name: string }[] = [
  { id: 'ground', name: 'GROUND' },
  { id: 'things', name: 'THINGS' },
  { id: 'items', name: 'ITEMS' },
  { id: 'animals', name: 'ANIMALS' },
];

/** Which palette tab a piece is under. */
function categoryOf(p: Piece): Category {
  if (p.kind === 'pickup') return 'items';
  if (p.kind === 'critter') return 'animals';
  return isSolid(TILE_BY_ID[p.id].kind) ? 'things' : 'ground';
}

const TOP_H = 18;
const CHIP = 21;
const CHIP_GAP = 2;
const CHIPS_PER_ROW = 8;
const CHIP_ROWS = 2;
/**
 * Height of everything under the lawn: tabs, chips, tools, size and yard, a line of status, the
 * button. A longer status wraps and the panel grows by a line for each extra one.
 */
const PANEL_H = 13 + CHIP_ROWS * (CHIP + CHIP_GAP) + 4 + 16 + 4 + 16 + 4 + 11 + 26 + 6;
const MESSAGE_LINE_H = 9;
const UNDO_DEPTH = 60;
/** The smallest a tile is drawn zoomed out (game px), so the lawn stays paintable. */
const MIN_TILE_PX = 6;

/**
 * The lawn builder (owner idea, 2026-10-02; lm-ocm.6): a lawn drawn as in play, a palette of the
 * pieces met in the campaign, tap or drag to paint, the start tapped to turn it, size and yard,
 * a rerollable name, undo, and CHECK (the solver must clear it) before PLAY. Every change is
 * saved as it is made.
 */
export class BuilderScene extends Phaser.Scene {
  private lawn!: CustomLawn;
  private history: CustomLawn[] = [];
  private category: Category = 'ground';
  private page = 0;
  private piece: Piece = { kind: 'tile', id: 'rock' };
  private mode: ToolMode = 'paint';
  private scrollY = 0;
  private message = '';
  private lawnLayer: Phaser.GameObjects.Container | null = null;
  /** The stroke in progress: the last tile painted, and whether undo has its snapshot. */
  private stroke: { x: number; y: number; started: boolean } | null = null;
  private panFrom: { py: number; scrollY: number } | null = null;
  /**
   * How big a tile is drawn (game px): TILE_SIZE at full size, smaller zoomed out (owner,
   * 2026-10-21: see a whole 12x24 lawn at once). Whole pixels, so the tiles stay on the grid.
   */
  private tilePx = TILE_SIZE;
  /** Zoomed out to fit the whole lawn (kept fitted as the lawn or the panel changes size). */
  private fitted = false;
  /** Fingers down on the lawn, for the pinch. */
  private readonly touches = new Map<number, { x: number; y: number }>();
  /** A pinch in progress: where it began. */
  private pinch: { dist: number; tilePx: number; worldY: number } | null = null;
  /** A finger left down after a pinch: it neither paints nor pans until all are up. */
  private afterPinch = false;

  constructor() {
    super('Builder');
  }

  create(data: BuilderData = {}): void {
    const save = saves().save;
    const id = data.fresh
      ? undefined
      : (data.lawnId ?? (this.registry.get(REG_BUILDER_LAWN) as string | undefined));
    const found = id ? save.customLawns.find((l) => l.id === id) : undefined;
    if (found) this.lawn = found;
    else {
      this.lawn = { ...newCustomLawn(save), name: lawnName(Math.floor(Math.random() * 400)) };
      saves().update((s) => storeLawn(s, this.lawn));
    }
    this.registry.set(REG_BUILDER_LAWN, this.lawn.id);
    this.history = [];
    this.category = 'ground';
    this.page = 0;
    this.mode = 'paint';
    this.piece = { kind: 'tile', id: 'rock' };
    this.tilePx = TILE_SIZE;
    this.fitted = false;
    this.touches.clear();
    this.pinch = null;
    this.afterPinch = false;
    this.message = this.lawn.par ? '' : 'Paint a lawn, then CHECK it.';
    // A tall lawn opens at its foot, where a new lawn's mower starts.
    this.scrollY = this.maxScroll();
    this.cameras.main.setBackgroundColor('#2b4a26');
    // Two fingers pinch (Phaser tracks one touch unless asked for more).
    if (this.input.manager.pointersTotal < 2) this.input.addPointer(1);
    // Moves and releases anywhere: a finger may leave the lawn mid-pinch.
    const move = (p: Phaser.Input.Pointer) => {
      if (p.isDown) this.drag(p);
    };
    const up = (p: Phaser.Input.Pointer) => this.release(p);
    const wheel = (_p: unknown, _o: unknown, _dx: number, dy: number) =>
      this.scrollTo(this.scrollY + dy * 0.5);
    this.input.on('pointermove', move);
    this.input.on('pointerup', up);
    this.input.on('pointerupoutside', up);
    this.input.on('wheel', wheel);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off('pointermove', move);
      this.input.off('pointerup', up);
      this.input.off('pointerupoutside', up);
      this.input.off('wheel', wheel);
    });
    // Redrawn when the screen's height changes (rotation, the safe area settling).
    const redraw = () => this.draw();
    this.scale.on(Phaser.Scale.Events.RESIZE, redraw);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.scale.off(Phaser.Scale.Events.RESIZE, redraw),
    );
    this.draw();
  }

  // ── Layout ─────────────────────────────────────────────────────────────────────────────────

  /** How many lines the status takes, wrapped (at most three). */
  private messageLines(): number {
    if (!this.message) return 1;
    return Math.min(3, wrapLineCount(this.message, this.scale.gameSize.width - 8));
  }

  private panelH(): number {
    return PANEL_H + (this.messageLines() - 1) * MESSAGE_LINE_H;
  }

  private view(): { x: number; top: number; bottom: number } {
    const { width, height } = this.scale.gameSize;
    const { width: w } = lawnSize(this.lawn);
    return {
      x: Math.floor((width - w * this.tilePx) / 2),
      top: TOP_H + 2,
      bottom: height - this.panelH(),
    };
  }

  /** The tile size that shows the whole lawn (full size when it already fits). */
  private fitTilePx(): number {
    const { width, height } = this.scale.gameSize;
    const { width: w, height: h } = lawnSize(this.lawn);
    const room = height - this.panelH() - (TOP_H + 2) - 4;
    const fit = Math.min(TILE_SIZE, Math.floor(room / h), Math.floor((width - 4) / w));
    return Math.max(MIN_TILE_PX, fit);
  }

  private maxScroll(): number {
    const v = this.view();
    return Math.max(0, lawnSize(this.lawn).height * this.tilePx - (v.bottom - v.top));
  }

  /** Draws tiles `px` big, keeping the lawn row in the middle of the view where it is. */
  private zoomTo(px: number): void {
    const fit = this.fitTilePx();
    const next = Math.max(fit, Math.min(TILE_SIZE, Math.round(px)));
    const v = this.view();
    const at = (v.bottom - v.top) / 2;
    const worldY = (this.scrollY + at) / this.tilePx;
    this.fitted = next === fit && next < TILE_SIZE;
    this.tilePx = next;
    this.scrollY = Math.round(Math.max(0, Math.min(this.maxScroll(), worldY * next - at)));
    this.draw();
  }

  private draw(): void {
    this.tweens.killAll();
    clearScene(this);
    this.lawnLayer = null;
    // Zoomed out to fit: still fitting (the lawn or the panel may have changed size).
    const fit = this.fitTilePx();
    if (this.fitted) this.tilePx = fit;
    this.tilePx = Math.max(fit, Math.min(TILE_SIZE, this.tilePx));
    this.fitted = this.fitted && this.tilePx < TILE_SIZE;
    this.scrollY = Math.max(0, Math.min(this.maxScroll(), this.scrollY));
    this.drawLawn();
    this.drawTop();
    this.drawPanel();
    this.listenToLawn();
  }

  // ── The lawn ───────────────────────────────────────────────────────────────────────────────

  /** The lawn as in play: its tiles, props, pickups, critters and the mower at the start. */
  private drawLawn(): void {
    this.lawnLayer?.destroy();
    const v = this.view();
    const layer = this.add
      .container(v.x, v.top - this.scrollY)
      .setScale(this.tilePx / TILE_SIZE)
      .setDepth(0);
    this.lawnLayer = layer;
    const { width: w, height: h } = lawnSize(this.lawn);
    const T = TILE_SIZE;
    // The fence line round it.
    const edge = this.add.graphics();
    edge.fillStyle(0xf4f1e6).fillRect(-2, -2, w * T + 4, h * T + 4);
    layer.add(edge);
    const standing: Phaser.GameObjects.GameObject[][] = Array.from({ length: h }, () => []);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const id = tileAt(this.lawn, x, y);
        const def = TILE_BY_ID[id];
        const prop = propFor(id);
        const frame = prop
          ? frameIndex('lawnBase')
          : def.kind === Tile.Hose
            ? frameIndex('hose')
            : tileFrame(def.kind, Dir.N, id, x, y);
        layer.add(this.add.image(x * T, y * T, TILES_KEY, frame).setOrigin(0));
        if (prop) {
          standing[y]!.push(
            this.add
              .image((x + 0.5) * T, (y + 1) * T - PROP_LIFT, PROPS_KEY, propFrame(prop))
              .setOrigin(0.5, 1),
          );
        }
        if (id === 'tallGrass') {
          standing[y]!.push(
            this.add.image((x + 0.5) * T, (y + 1) * T, BLADES_KEY, 0).setOrigin(0.5, 1),
          );
        }
      }
    }
    for (const p of this.lawn.pickups) {
      standing[p.y]?.push(
        this.add.image((p.x + 0.5) * T, (p.y + 0.5) * T, PICKUPS_KEY, PICKUP_FRAME[p.kind]),
      );
    }
    for (const c of this.lawn.critters) {
      standing[c.y]?.push(
        this.add.image((c.x + 0.5) * T, (c.y + 0.5) * T, CRITTERS_KEY, CRITTER_FRAME[c.kind]),
      );
    }
    // The mower on the start, facing the way it sets off.
    const s = startOf(this.lawn);
    const dir = { N: Dir.N, E: Dir.E, S: Dir.S, W: Dir.W }[this.lawn.startDir];
    const mower: MowerId = saves().save.selectedMower;
    standing[s.y]!.push(
      this.add.image((s.x + 0.5) * T, (s.y + 0.5) * T, mowerTextureKey(mower), dir),
    );
    // Row by row, so things further down stand in front.
    for (const row of standing) layer.add(row);
    if (this.mode === 'start') {
      const mark = this.add.graphics();
      mark.lineStyle(1, UI.accent).strokeRect(s.x * T + 0.5, s.y * T + 0.5, T - 1, T - 1);
      layer.add(mark);
    }
  }

  /**
   * Touch on the lawn: paint, erase, move or turn the start, or pan; two fingers pinch to zoom.
   * (Moves and releases are heard scene-wide, from create.)
   */
  private listenToLawn(): void {
    const v = this.view();
    const { width } = this.scale.gameSize;
    const zone = this.add
      .zone(0, v.top, width, v.bottom - v.top)
      .setOrigin(0)
      .setInteractive();
    zone.on('pointerdown', (p: Phaser.Input.Pointer) => this.press(p));
    // Off the lawn (onto the panel) a stroke ends: the lawn under the panel is out of sight.
    zone.on('pointerout', () => {
      this.stroke = null;
      this.panFrom = null;
    });
  }

  private tileUnder(p: Phaser.Input.Pointer): { x: number; y: number } | null {
    const v = this.view();
    if (p.y < v.top || p.y >= v.bottom) return null;
    const x = Math.floor((p.x - v.x) / this.tilePx);
    const y = Math.floor((p.y - v.top + this.scrollY) / this.tilePx);
    const { width: w, height: h } = lawnSize(this.lawn);
    return x >= 0 && y >= 0 && x < w && y < h ? { x, y } : null;
  }

  /** The first two fingers down: how far apart, and their middle's height. */
  private twoFingers(): { dist: number; midY: number } | null {
    const [a, b] = [...this.touches.values()];
    if (!a || !b) return null;
    return { dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), midY: (a.y + b.y) / 2 };
  }

  private press(p: Phaser.Input.Pointer): void {
    this.touches.set(p.id, { x: p.x, y: p.y });
    if (this.touches.size >= 2) {
      this.startPinch();
      return;
    }
    if (this.afterPinch) return;
    if (this.mode === 'pan') {
      this.panFrom = { py: p.y, scrollY: this.scrollY };
      return;
    }
    const at = this.tileUnder(p);
    if (!at) return;
    if (this.mode === 'start') {
      this.apply(moveStart(this.lawn, at.x, at.y));
      return;
    }
    this.stroke = { x: -1, y: -1, started: false };
    this.paintAt(at.x, at.y);
  }

  /**
   * A second finger: a pinch begins. The first finger's stroke is taken back (it was the start
   * of the pinch, not painting).
   */
  private startPinch(): void {
    const two = this.twoFingers();
    if (!two || this.pinch) return;
    if (this.stroke?.started) {
      const before = this.history.pop();
      if (before) {
        this.lawn = before;
        saves().update((s) => storeLawn(s, before));
        this.drawLawn();
      }
    }
    this.stroke = null;
    this.panFrom = null;
    const v = this.view();
    this.pinch = {
      dist: two.dist,
      tilePx: this.tilePx,
      worldY: (this.scrollY + two.midY - v.top) / this.tilePx,
    };
  }

  /** Pinching: the fingers' spread sets the zoom; their middle holds its row (and pans). */
  private pinchMove(): void {
    const two = this.twoFingers();
    const pinch = this.pinch;
    if (!two || !pinch) return;
    const v = this.view();
    const fit = this.fitTilePx();
    const next = Math.max(
      fit,
      Math.min(TILE_SIZE, Math.round((pinch.tilePx * two.dist) / pinch.dist)),
    );
    const zoomed = next !== this.tilePx;
    this.tilePx = next;
    this.fitted = next === fit && next < TILE_SIZE;
    const y = pinch.worldY * next - (two.midY - v.top);
    if (zoomed) {
      this.scrollY = Math.round(Math.max(0, Math.min(this.maxScroll(), y)));
      this.draw();
    } else this.scrollTo(y);
  }

  private drag(p: Phaser.Input.Pointer): void {
    if (this.touches.has(p.id)) this.touches.set(p.id, { x: p.x, y: p.y });
    if (this.pinch) {
      this.pinchMove();
      return;
    }
    if (this.afterPinch) return;
    if (this.mode === 'pan' && this.panFrom) {
      this.scrollTo(this.panFrom.scrollY - (p.y - this.panFrom.py));
      return;
    }
    if (!this.stroke) return;
    const at = this.tileUnder(p);
    if (at && (at.x !== this.stroke.x || at.y !== this.stroke.y)) this.paintAt(at.x, at.y);
  }

  private release(p: Phaser.Input.Pointer): void {
    this.touches.delete(p.id);
    if (this.pinch && this.touches.size < 2) {
      this.pinch = null;
      // The finger still down was half of the pinch, not the start of a stroke.
      this.afterPinch = this.touches.size > 0;
      this.draw();
    }
    if (this.touches.size === 0) this.afterPinch = false;
    this.stroke = null;
    this.panFrom = null;
  }

  private scrollTo(y: number): void {
    const next = Math.round(Math.max(0, Math.min(this.maxScroll(), y)));
    if (next === this.scrollY) return;
    this.scrollY = next;
    if (this.lawnLayer) this.lawnLayer.y = this.view().top - this.scrollY;
  }

  /** One tile of a stroke: the piece (or grass, erasing). A stroke is one step of undo. */
  private paintAt(x: number, y: number): void {
    const stroke = this.stroke!;
    stroke.x = x;
    stroke.y = y;
    const next =
      this.mode === 'erase' ? erase(this.lawn, x, y) : placePiece(this.lawn, this.piece, x, y);
    if (next === this.lawn || JSON.stringify(next) === JSON.stringify(this.lawn)) return;
    this.apply(next, stroke.started);
    stroke.started = true;
  }

  /** Takes an edit: undo keeps the lawn before it (once per stroke), and it is saved. */
  private apply(next: CustomLawn, sameStroke = false): void {
    if (next === this.lawn) return;
    if (!sameStroke) this.history = [...this.history, this.lawn].slice(-UNDO_DEPTH);
    this.lawn = next;
    saves().update((s) => storeLawn(s, next));
    if (!next.par && this.message.startsWith('Clears')) this.message = '';
    // While painting only the lawn changes; the panel's button may change with the check.
    this.drawLawn();
    this.redrawPanelSoon();
  }

  private panelDirty = false;
  private redrawPanelSoon(): void {
    if (this.panelDirty) return;
    this.panelDirty = true;
    this.time.delayedCall(0, () => {
      this.panelDirty = false;
      if (!this.stroke) this.draw();
    });
  }

  private undo(): void {
    const prev = this.history.pop();
    if (!prev) return;
    this.lawn = prev;
    saves().update((s) => storeLawn(s, prev));
    this.scrollTo(Math.min(this.scrollY, this.maxScroll()));
    this.draw();
  }

  // ── The top bar ────────────────────────────────────────────────────────────────────────────

  private drawTop(): void {
    const { width } = this.scale.gameSize;
    const g = this.add.graphics().setDepth(10);
    g.fillStyle(UI.panel).fillRect(0, 0, width, TOP_H);
    g.fillStyle(UI.rule).fillRect(0, TOP_H - 1, width, 1);
    const back = label(this, 6, 5, '< LAWNS', 1, UI.text).setDepth(11);
    this.add
      .zone(0, 0, back.width + 12, TOP_H)
      .setOrigin(0)
      .setDepth(11)
      .setData('label', 'BACK')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => wipeTo(this, 'MyLawns', {}));
    // The name: tap for another.
    const name = label(this, width - 6, 5, this.lawn.name.toUpperCase(), 1, UI.accent)
      .setOrigin(1, 0)
      .setDepth(11);
    this.add
      .zone(name.x - name.width - 4, 0, name.width + 10, TOP_H)
      .setOrigin(0)
      .setDepth(11)
      .setData('label', 'RENAME')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        this.apply({ ...this.lawn, name: lawnName(Math.floor(Math.random() * 400)) });
        this.draw();
      });
  }

  // ── The panel under the lawn ───────────────────────────────────────────────────────────────

  private drawPanel(): void {
    const { width, height } = this.scale.gameSize;
    const panelH = this.panelH();
    const top = height - panelH;
    const g = this.add.graphics().setDepth(10);
    g.fillStyle(0x15102f).fillRect(0, top, width, panelH);
    g.fillStyle(UI.rule).fillRect(0, top, width, 1);
    let y = top + 3;
    this.drawTabs(y, width);
    y += 13;
    this.drawChips(y, width);
    y += CHIP_ROWS * (CHIP + CHIP_GAP) + 4;
    this.drawTools(y, width);
    y += 20;
    this.drawSize(y, width);
    y += 20;
    // The status, wrapped (owner, 2026-10-21: a long one ran off both edges).
    if (this.message) {
      label(this, width / 2, y, this.message, 1, UI.accent)
        .setMaxWidth(width - 8)
        .setCenterAlign()
        .setLineSpacing(MESSAGE_LINE_H - 8)
        .setOrigin(0.5, 0)
        .setDepth(11);
    }
    y += 11 + (this.messageLines() - 1) * MESSAGE_LINE_H;
    this.drawAction(y, width);
  }

  private drawTabs(y: number, width: number): void {
    const w = Math.floor(width / CATEGORIES.length);
    CATEGORIES.forEach((c, i) => {
      const on = c.id === this.category && this.mode === 'paint';
      const t = label(this, i * w + w / 2, y, c.name, 1, on ? UI.accent : UI.dim)
        .setOrigin(0.5, 0)
        .setDepth(11);
      if (on)
        this.add
          .graphics()
          .setDepth(11)
          .fillStyle(UI.accent)
          .fillRect(t.x - t.width / 2, y + 9, t.width, 1);
      this.add
        .zone(i * w, y - 2, w, 12)
        .setOrigin(0)
        .setDepth(11)
        .setData('label', c.name)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          this.category = c.id;
          this.page = 0;
          this.mode = 'paint';
          this.draw();
        });
    });
  }

  /** The category's pieces: usable ones first; those in kits not bought yet dimmed and locked. */
  private drawChips(top: number, width: number): void {
    const save = saves().save;
    const pieces = ALL_PIECES.filter(
      (p) => categoryOf(p) === this.category && FIRST_MET.has(pieceKey(p)),
    )
      .map((p) => ({ p, met: pieceUsable(save, p) }))
      .sort((a, b) => Number(b.met) - Number(a.met));
    const perPage = CHIPS_PER_ROW * CHIP_ROWS;
    // A full page and more: the last chip turns the page.
    const paged = pieces.length > perPage;
    const shownPer = paged ? perPage - 1 : perPage;
    const pages = Math.max(1, Math.ceil(pieces.length / shownPer));
    const page = Math.min(this.page, pages - 1);
    const shown = pieces.slice(page * shownPer, (page + 1) * shownPer);
    const x0 = Math.floor((width - (CHIPS_PER_ROW * (CHIP + CHIP_GAP) - CHIP_GAP)) / 2);
    const at = (i: number) => ({
      x: x0 + (i % CHIPS_PER_ROW) * (CHIP + CHIP_GAP),
      y: top + Math.floor(i / CHIPS_PER_ROW) * (CHIP + CHIP_GAP),
    });
    const g = this.add.graphics().setDepth(11);
    shown.forEach(({ p, met }, i) => {
      const { x, y } = at(i);
      const on = this.mode === 'paint' && pieceKey(p) === pieceKey(this.piece);
      g.fillStyle(on ? UI.panelHi : UI.panel).fillRect(x, y, CHIP, CHIP);
      g.lineStyle(1, on ? UI.accent : UI.rule).strokeRect(x + 0.5, y + 0.5, CHIP - 1, CHIP - 1);
      const icon = this.pieceIcon(p, x + CHIP / 2, y + CHIP / 2);
      if (!met) {
        // Its kit not bought yet: dimmed, with a padlock (tap to hear which kit has it).
        icon.setAlpha(0.3);
        drawPadlock(this.add.graphics().setDepth(12), x + CHIP - 10, y + CHIP - 11);
      }
      this.add
        .zone(x, y, CHIP, CHIP)
        .setOrigin(0)
        .setDepth(12)
        .setData('label', pieceKey(p))
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => this.pick(p, met));
    });
    if (paged) {
      const { x, y } = at(perPage - 1);
      g.fillStyle(UI.panel).fillRect(x, y, CHIP, CHIP);
      g.lineStyle(1, UI.rule).strokeRect(x + 0.5, y + 0.5, CHIP - 1, CHIP - 1);
      label(this, x + CHIP / 2, y + 7, '>', 1, UI.text)
        .setOrigin(0.5, 0)
        .setDepth(12);
      this.add
        .zone(x, y, CHIP, CHIP)
        .setOrigin(0)
        .setDepth(12)
        .setData('label', 'MORE')
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          this.page = (page + 1) % pages;
          this.draw();
        });
    }
  }

  /** A piece's icon, centered at (cx, cy), scaled to sit in a chip. */
  private pieceIcon(p: Piece, cx: number, cy: number): Phaser.GameObjects.Image {
    let img: Phaser.GameObjects.Image;
    if (p.kind === 'pickup') img = this.add.image(cx, cy, PICKUPS_KEY, PICKUP_FRAME[p.id]);
    else if (p.kind === 'critter') img = this.add.image(cx, cy, CRITTERS_KEY, CRITTER_FRAME[p.id]);
    else {
      const prop = propFor(p.id);
      if (prop) {
        img = this.add
          .image(cx, cy + 9, PROPS_KEY, propFrame(prop))
          .setOrigin(0.5, 1)
          .setScale(0.6);
      } else {
        const def = TILE_BY_ID[p.id];
        const frame =
          def.kind === Tile.Hose ? frameIndex('hose') : tileFrame(def.kind, Dir.N, p.id, 0, 0);
        img = this.add.image(cx, cy, TILES_KEY, frame);
      }
    }
    return img.setDepth(12);
  }

  private pick(p: Piece, met: boolean): void {
    if (!met) {
      const zone = kitOf(p);
      const name = BIOMES.find((b) => b.id === zone)?.name ?? '';
      this.message = `In the ${name} kit (My Lawns).`;
      this.draw();
      return;
    }
    this.piece = p;
    this.mode = 'paint';
    this.message = '';
    this.draw();
  }

  /**
   * ERASE, START (move or turn it), PAN (scroll a tall lawn), FIT (zoom out to the whole lawn, or
   * back to full size; two fingers also pinch), UNDO.
   */
  private drawTools(y: number, width: number): void {
    const zoomedOut = this.tilePx < TILE_SIZE;
    const tools: { text: string; on: boolean; action: () => void; enabled?: boolean }[] = [
      { text: 'ERASE', on: this.mode === 'erase', action: () => this.setMode('erase') },
      { text: 'START', on: this.mode === 'start', action: () => this.setMode('start') },
      {
        text: 'PAN',
        on: this.mode === 'pan',
        action: () => this.setMode('pan'),
        enabled: this.maxScroll() > 0,
      },
      {
        text: 'FIT',
        on: zoomedOut,
        action: () => this.zoomTo(zoomedOut ? TILE_SIZE : this.fitTilePx()),
        enabled: zoomedOut || this.fitTilePx() < TILE_SIZE,
      },
      { text: 'UNDO', on: false, action: () => this.undo(), enabled: this.history.length > 0 },
    ];
    const gap = 2;
    const w = Math.floor((width - 8 - gap * (tools.length - 1)) / tools.length);
    tools.forEach((t, i) => {
      const enabled = t.enabled ?? true;
      bevelButton(this, 4 + i * (w + gap), y, w, 16, t.text, () => enabled && t.action(), t.on)
        .setDepth(11)
        .setAlpha(enabled ? 1 : 0.4);
    });
  }

  private setMode(mode: ToolMode): void {
    this.mode = this.mode === mode ? 'paint' : mode;
    this.message =
      this.mode === 'start'
        ? 'Tap a tile for the start; tap it to turn.'
        : this.mode === 'pan'
          ? 'Drag to look up and down.'
          : '';
    this.draw();
  }

  /** The size (W and H steppers) and the yard it sits in. */
  private drawSize(y: number, width: number): void {
    const { width: w, height: h } = lawnSize(this.lawn);
    const step = (x: number, text: string, action: () => void) =>
      bevelButton(this, x, y, 16, 16, text, action).setDepth(11);
    step(4, '-', () => this.resize(w - 1, h));
    label(this, 30, y + 5, `W${w}`, 1, UI.text)
      .setOrigin(0.5, 0)
      .setDepth(11);
    step(40, '+', () => this.resize(w + 1, h));
    step(62, '-', () => this.resize(w, h - 1));
    label(this, 88, y + 5, `H${h}`, 1, UI.text)
      .setOrigin(0.5, 0)
      .setDepth(11);
    step(98, '+', () => this.resize(w, h + 1));
    // The yard: its icon and name; tap for the next one reached.
    const yard = BIOMES.find((b) => b.id === this.lawn.biome)!;
    const g = this.add.graphics().setDepth(11);
    drawWorldIcon(g, yard.id, width - 64, y + 3);
    label(this, width - 52, y + 5, 'YARD', 1, UI.dim).setDepth(11);
    this.add
      .zone(width - 68, y, 64, 16)
      .setOrigin(0)
      .setDepth(11)
      .setData('label', 'YARD')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.nextYard());
  }

  private resize(w: number, h: number): void {
    const next = resizeLawn(this.lawn, w, h);
    const now = lawnSize(this.lawn);
    const size = lawnSize(next);
    if (size.width === now.width && size.height === now.height) return;
    this.apply(next);
    this.scrollTo(Math.min(this.scrollY, this.maxScroll()));
    this.draw();
  }

  /** The yards reached in the campaign, in order (the Backyard always). */
  private nextYard(): void {
    const save = saves().save;
    const reached = BIOMES.filter(
      (b, i) =>
        i === 0 || ((save.levels[campaignLevelId(b.levels[0])]?.stars ?? 0) & STAR.clear) !== 0,
    );
    const i = reached.findIndex((b) => b.id === this.lawn.biome);
    const next: BiomeId = reached[(i + 1) % reached.length]!.id;
    this.message = `${BIOMES.find((b) => b.id === next)!.name}.`;
    this.apply({ ...this.lawn, biome: next });
    this.draw();
  }

  /** CHECK runs the solver; once it clears the lawn, PLAY. */
  private drawAction(y: number, width: number): void {
    if (this.lawn.par) {
      const play = () => {
        if (playCustomLawn(this, this.lawn)) return;
        this.message = 'None of your mowers clear it.';
        this.draw();
      };
      // PLAY, and SHARE (a link: the code is the lawn, no server).
      const shareW = 56;
      bevelButton(this, 6, y, width - 16 - shareW, 26, 'PLAY', play, true, 2).setDepth(11);
      bevelButton(this, width - 6 - shareW, y, shareW, 26, 'SHARE', () => this.share()).setDepth(
        11,
      );
      return;
    }
    bevelButton(this, 6, y, width - 12, 26, 'CHECK', () => this.check(), true, 2).setDepth(11);
  }

  /** The share sheet with the lawn's link (or the link copied, where there is no sheet). */
  private share(): void {
    const name = this.lawn.name;
    void shareLink('lawnmower.', `Mow my lawn "${name}" in lawnmower.!`, lawnLink(this.lawn)).then(
      (outcome) => {
        if (!this.scene.isActive()) return;
        this.message =
          outcome === 'copied'
            ? 'Link copied: paste it to a friend.'
            : outcome === 'failed'
              ? 'Could not share it here.'
              : outcome === 'shared'
                ? 'Shared!'
                : '';
        this.draw();
      },
    );
  }

  private check(): void {
    this.message = 'Checking...';
    this.draw();
    // (A frame for the message to show; the solver takes a moment on a big lawn.)
    this.time.delayedCall(30, () => {
      const result = checkLawn(this.lawn);
      if (result.problems.length > 0) {
        this.message = result.problems[0]!;
        this.draw();
        return;
      }
      const lawn = { ...this.lawn, par: result.par };
      this.lawn = lawn;
      saves().update((s) => storeLawn(s, lawn));
      const names = Object.keys(result.par).map(
        (id) => MOWER_DEFS[id as MowerId].name.split(' ')[0],
      );
      this.message = names.length >= 5 ? 'Clears with every mower!' : `Clears: ${names.join(', ')}`;
      this.draw();
    });
  }
}
